/**
 * Log dedicado do Registrar (cliente) + ponte para a trilha server (aprovações).
 *
 * Persistência local: localStorage por setor (UX / histórico imediato neste navegador).
 * Trilha server: após confirm, as ações vão para `mends_vision_approvals` (source=register).
 * Os `approvalIds` no log ligam o relato local à auditoria multi-usuário no backend.
 *
 * Limite honesto (REGX-05 / REG-40): sem API REST de log multi-device — outro browser/dispositivo
 * não vê o histórico local; a trilha compartilhada é a fila de aprovações (IDs + status via listApprovals).
 */

export type RegisterLogStatus =
  | 'draft'
  | 'awaiting_clarification'
  | 'confirmed'
  | 'failed';

export interface RegisterLogEntry {
  id: string;
  sectorId: number;
  status: RegisterLogStatus;
  rawTranscript: string;
  summary?: string | null;
  contactId?: number | null;
  contactName?: string | null;
  actionTitles?: string[];
  approvalsCreated?: number;
  approvalIds?: number[];
  memorySaved?: boolean;
  candidates?: Array<{ id: number; name: string }>;
  errorMessage?: string | null;
  /** Origem da abertura (cliente / inbox / oportunidade / global…). */
  sourceSurface?: string | null;
  conversationId?: number | null;
  opportunityId?: string | null;
  createdAt: string;
  updatedAt: string;
  /** Sempre local; server trail via approvalIds. */
  source: 'client_register_log';
}

/** Status server cruzado a partir de listApprovals (quando disponível). */
export interface RegisterApprovalServerStatus {
  id: number;
  status: string;
  title?: string | null;
  contactName?: string | null;
}

export interface RegisterLogEntryWithServer extends RegisterLogEntry {
  serverApprovals: RegisterApprovalServerStatus[];
  /** Resumo curto p/ UI: pending | mixed | approved | rejected | needs_info | none */
  serverTrailSummary: string;
}

const PREFIX = 'mends.registerLog.v1.';
const MAX_ENTRIES = 80;

function key(sectorId: number) {
  return `${PREFIX}${sectorId}`;
}

function readAll(sectorId: number): RegisterLogEntry[] {
  try {
    const raw = localStorage.getItem(key(sectorId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeAll(sectorId: number, entries: RegisterLogEntry[]) {
  try {
    localStorage.setItem(key(sectorId), JSON.stringify(entries.slice(0, MAX_ENTRIES)));
  } catch {
    /* quota / private mode — silencioso */
  }
}

function uid() {
  return `reg_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export function listRegisterLog(sectorId: number): RegisterLogEntry[] {
  return readAll(sectorId).slice().sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export function appendRegisterLog(
  sectorId: number,
  input: Omit<RegisterLogEntry, 'id' | 'createdAt' | 'updatedAt' | 'source' | 'sectorId'> & {
    id?: string;
  },
): RegisterLogEntry {
  const now = new Date().toISOString();
  const existing = readAll(sectorId);
  const id = input.id || uid();
  const prev = existing.find((e) => e.id === id);
  const entry: RegisterLogEntry = {
    id,
    sectorId,
    status: input.status,
    rawTranscript: input.rawTranscript,
    summary: input.summary ?? null,
    contactId: input.contactId ?? null,
    contactName: input.contactName ?? null,
    actionTitles: input.actionTitles || [],
    approvalsCreated: input.approvalsCreated,
    approvalIds: input.approvalIds || prev?.approvalIds,
    memorySaved: input.memorySaved ?? prev?.memorySaved,
    candidates: input.candidates,
    errorMessage: input.errorMessage ?? null,
    sourceSurface: input.sourceSurface ?? prev?.sourceSurface ?? null,
    conversationId: input.conversationId ?? prev?.conversationId ?? null,
    opportunityId: input.opportunityId ?? prev?.opportunityId ?? null,
    createdAt: prev?.createdAt || now,
    updatedAt: now,
    source: 'client_register_log',
  };
  const next = [entry, ...existing.filter((e) => e.id !== id)].slice(0, MAX_ENTRIES);
  writeAll(sectorId, next);
  return entry;
}

function summarizeServerStatuses(statuses: string[]): string {
  if (!statuses.length) return 'none';
  const set = new Set(statuses.map((s) => (s || '').toLowerCase()));
  if (set.size === 1) {
    const only = Array.from(set)[0];
    if (only === 'approved' || only === 'executed') return 'approved';
    if (only === 'rejected' || only === 'expired' || only === 'superseded') return 'rejected';
    if (only === 'needs_info') return 'needs_info';
    if (only === 'pending' || only === 'proposed' || only === 'edited') return 'pending';
    return only || 'mixed';
  }
  return 'mixed';
}

/**
 * Enriquece o log local com status atual das aprovações ligadas (bridge REGX-05).
 * Não altera localStorage — só leitura cruzada para a UI.
 */
export function enrichRegisterLogWithApprovals(
  entries: RegisterLogEntry[],
  approvals: Array<{
    id: number;
    status: string;
    title?: string | null;
    contactName?: string | null;
  }>,
): RegisterLogEntryWithServer[] {
  const byId = new Map(approvals.map((a) => [a.id, a]));
  return entries.map((entry) => {
    const ids = entry.approvalIds || [];
    const serverApprovals: RegisterApprovalServerStatus[] = ids
      .map((id) => {
        const a = byId.get(id);
        if (!a) return { id, status: 'unknown', title: null, contactName: null };
        return {
          id: a.id,
          status: a.status,
          title: a.title ?? null,
          contactName: a.contactName ?? null,
        };
      });
    return {
      ...entry,
      serverApprovals,
      serverTrailSummary: summarizeServerStatuses(
        serverApprovals.map((s) => s.status).filter((s) => s !== 'unknown'),
      ),
    };
  });
}

export function serverTrailLabel(summary: string): string {
  switch (summary) {
    case 'pending':
      return 'Fila: aguardando aprovação';
    case 'approved':
      return 'Fila: aprovado / executado';
    case 'rejected':
      return 'Fila: rejeitado / expirado';
    case 'needs_info':
      return 'Fila: precisa de esclarecimento';
    case 'mixed':
      return 'Fila: status mistos';
    case 'none':
      return 'Sem aprovações vinculadas';
    default:
      return summary === 'unknown' ? 'Fila: status indisponível' : `Fila: ${summary}`;
  }
}
